import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useEyeconxStore } from '../../../store/eyeconxStore';
import clinicService from '../../../services/clinic.service';
import SubHeader from '../../layout/components/SubHeader';
import Pagination from '../../common/Pagination';
import ActivityGroupedList from './subparts/ActivityGroupedList';
import ActivityDetailsModal from './subparts/ActivityDetailsModal';
import ActivitySkeleton from './subparts/ActivitySkeleton';
import ActivityFilters from './subparts/ActivityFilters';
import ActivityCategoryPanel from './subparts/ActivityCategoryPanel';
import {
    ACTIVITY_SORT_FIELDS,
    DEFAULT_ACTIVITY_FILTERS,
    DEFAULT_ACTIVITY_SORT,
    buildActivityQuery,
    formatActivityCount,
} from './subparts/activityQuery';
import './Activity.css';

const PAGE_SIZE_OPTIONS = [10, 20, 50];

const Activity = () => {
    const clinicId = useEyeconxStore((state) => state.user?.clinicId);

    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [selectedActivity, setSelectedActivity] = useState(null);

    const [filters, setFilters] = useState(DEFAULT_ACTIVITY_FILTERS);
    const [appliedFilters, setAppliedFilters] = useState(DEFAULT_ACTIVITY_FILTERS);
    const [sort, setSort] = useState(DEFAULT_ACTIVITY_SORT);

    const [pageNumber, setPageNumber] = useState(0);
    const [pageSize, setPageSize] = useState(20);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);

    const fetchActivity = useCallback(
        async (page, size, nextFilters, nextSort, options = {}) => {
            const { silent = false } = options;

            if (!clinicId) {
                return;
            }

            if (!silent) {
                setLoading(true);
            }

            try {
                const query = buildActivityQuery(nextFilters, nextSort);
                const res = await clinicService.getActivityList(clinicId, page, size, query);
                const data = res.data;
                const content = data.content || [];

                setRecords(content);
                setPageNumber(data.number ?? page);
                setPageSize(data.size ?? size);
                setTotalPages(data.totalPages ?? 0);
                setTotalElements(data.totalElements ?? 0);
            } catch (err) {
                console.error('Failed to load clinic activity', err);
                toast.error('Failed to load clinic activity');
            } finally {
                if (!silent) {
                    setLoading(false);
                }
            }
        },
        [clinicId]
    );

    useEffect(() => {
        fetchActivity(pageNumber, pageSize, appliedFilters, sort);
    }, [fetchActivity, pageNumber, pageSize, appliedFilters, sort]);

    const handleFiltersChange = (updater) => {
        setFilters((prev) => (typeof updater === 'function' ? updater(prev) : updater));
    };

    const handleApplyFilters = (nextFilters = filters) => {
        setFilters(nextFilters);
        setAppliedFilters(nextFilters);
        setPageNumber(0);
    };

    const handleQuickFilter = (field, value) => {
        const next = { ...filters, [field]: value };

        if (field === 'date') {
            next.dateFrom = '';
            next.dateTo = '';
        }

        setFilters(next);
        setAppliedFilters(next);
        setPageNumber(0);
    };

    const handleClearFilters = () => {
        setFilters(DEFAULT_ACTIVITY_FILTERS);
        setAppliedFilters(DEFAULT_ACTIVITY_FILTERS);
        setSort(DEFAULT_ACTIVITY_SORT);
        setPageNumber(0);
    };

    const handleRemoveChip = (field, clearValue = '') => {
        const next = { ...appliedFilters, [field]: clearValue };
        setFilters(next);
        setAppliedFilters(next);
        setPageNumber(0);
    };

    const handleSortChange = (field, value) => {
        setSort((prev) => ({ ...prev, [field]: value }));
        setPageNumber(0);
    };

    const handlePageSizeChange = (event) => {
        setPageSize(Number(event.target.value));
        setPageNumber(0);
    };

    const handleRefresh = async () => {
        if (!clinicId || isRefreshing) {
            return;
        }

        setIsRefreshing(true);

        try {
            await fetchActivity(pageNumber, pageSize, appliedFilters, sort, { silent: true });
        } finally {
            setIsRefreshing(false);
        }
    };

    const description = useMemo(
        () =>
            totalElements
                ? `Clinic communication activity · ${formatActivityCount(totalElements)} total record${totalElements === 1 ? '' : 's'}`
                : 'Search and review clinic communication activity',
        [totalElements]
    );

    return (
        <div className="ecx-page activity-page ecx-page--full-width">
            <SubHeader
                title="Activity"
                description={description}
                icon="bi-clock-history"
                containerClass="w-100 px-4"
            >
                <div className="d-flex align-items-center gap-2">
                    <Link
                        to="/help?category=activity"
                        className="btn btn-sm btn-outline-primary rounded-pill"
                    >
                        <i className="bi bi-question-circle me-1"></i>
                        Help
                    </Link>
                    <button
                        type="button"
                        className="btn btn-light-primary btn-sm rounded-pill"
                        onClick={handleRefresh}
                        disabled={isRefreshing || loading}
                        title="Refresh activity"
                    >
                        <i className={`bi bi-arrow-clockwise me-1 ${isRefreshing ? 'ecx-refresh-spin' : ''}`}></i>
                        Refresh
                    </button>
                </div>
            </SubHeader>

            <div className="ecx-page-body">
                <div className="row g-3 activity-layout">
                    <div className="col-12 col-lg-3 col-xl-3 order-2 order-lg-1">
                        <ActivityCategoryPanel
                            selectedCategory={appliedFilters.category}
                            onCategoryChange={(value) => handleQuickFilter('category', value)}
                            loading={loading}
                        />
                    </div>

                    <div className="col-12 col-lg-9 col-xl-9 order-1 order-lg-2">
                        <div className="ecx-page-card ecx-page-panel activity-log-panel">
                            <ActivityFilters
                                filters={filters}
                                appliedFilters={appliedFilters}
                                onFiltersChange={handleFiltersChange}
                                onApply={handleApplyFilters}
                                onQuickFilter={handleQuickFilter}
                                onClear={handleClearFilters}
                                onRemoveChip={handleRemoveChip}
                                loading={loading}
                            />

                            <div className="ecx-page-panel__header border-top-0">
                                <div className="activity-toolbar">
                                    <div className="activity-toolbar__meta">
                                        <h5 className="mb-0 fw-semibold text-dark">Activity Log</h5>
                                        <span className="ecx-page-chip">
                                            <i className="bi bi-list-ul"></i>
                                            Page {totalPages === 0 ? 0 : pageNumber + 1} of {Math.max(totalPages, 1)}
                                        </span>
                                    </div>

                                    <div className="activity-toolbar__controls">
                                        <label className="d-flex align-items-center gap-2 mb-0 small text-muted">
                                            Sort
                                            <select
                                                className="form-select form-select-sm"
                                                style={{ width: '9.5rem' }}
                                                value={sort.sortBy}
                                                onChange={(e) => handleSortChange('sortBy', e.target.value)}
                                                disabled={loading}
                                            >
                                                {ACTIVITY_SORT_FIELDS.map((option) => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                            <select
                                                className="form-select form-select-sm"
                                                style={{ width: '6.5rem' }}
                                                value={sort.sortOrder}
                                                onChange={(e) => handleSortChange('sortOrder', e.target.value)}
                                                disabled={loading}
                                            >
                                                <option value="DESC">Desc</option>
                                                <option value="ASC">Asc</option>
                                            </select>
                                        </label>

                                        <label className="d-flex align-items-center gap-2 mb-0 small text-muted">
                                            Show
                                            <select
                                                className="form-select form-select-sm"
                                                style={{ width: '5rem' }}
                                                value={pageSize}
                                                onChange={handlePageSizeChange}
                                                disabled={loading}
                                            >
                                                {PAGE_SIZE_OPTIONS.map((size) => (
                                                    <option key={size} value={size}>
                                                        {size}
                                                    </option>
                                                ))}
                                            </select>
                                            entries
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <div className="ecx-page-panel__body d-flex flex-column">
                                {loading ? (
                                    <ActivitySkeleton count={pageSize > 5 ? 5 : pageSize} />
                                ) : records.length > 0 ? (
                                    <>
                                        <div className="flex-grow-1">
                                            <ActivityGroupedList
                                                records={records}
                                                onViewDetails={setSelectedActivity}
                                            />
                                        </div>

                                        {totalPages > 1 && (
                                            <div className="ecx-page-pagination border-top pt-3 mt-3">
                                                <Pagination
                                                    currentPage={pageNumber}
                                                    totalPages={totalPages}
                                                    totalElements={totalElements}
                                                    pageSize={pageSize}
                                                    onPageChange={setPageNumber}
                                                />
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div className="ecx-page-empty flex-grow-1">
                                        <div className="ecx-page-empty__icon">
                                            <i className="bi bi-clock-history"></i>
                                        </div>
                                        <h5 className="text-dark fw-semibold mb-2">No activity found</h5>
                                        <p className="mb-0">
                                            Try adjusting your filters or choose a different category to see clinic activity.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <ActivityDetailsModal
                show={Boolean(selectedActivity)}
                onClose={() => setSelectedActivity(null)}
                clinicId={clinicId}
                activityId={selectedActivity?.id}
                previewItem={selectedActivity}
            />
        </div>
    );
};

export default Activity;
